import { Grid, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectBasketId, selectOrderType, selectStrategyBuilderMode, selectTradeOrders, selectTradeProduct } from '../../../selectors/strategyBuilder'
import { Broker_OrderType, broker_OrderTypeToJSON, Order, OrderStatus, Product, TransactionType } from '../../../proto/generated/order'
import { updateOrderType, updateTradeProduct } from '../../../reducer/strategyBuilder.reducer'
import { NonInteractiveTooltip } from '../../custom'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { selectOrdersHistory } from '../../../selectors/orders'
import { selectUserDetails } from '../../../selectors/user'
import { isAdmin } from '../../../util/authentication'
import SlAndTargetInputs from '../slAndTargetInputs/slAndTargetInputs'
import { Position } from '../../../proto/generated/position'
import { getAnalyzedGroupedPosition } from '../../../util/positions'
import { GroupedPosition } from '../../../reducer/positions.reducer'
import useSelectPrimaryOrUnderlyingSecurity from '../../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { calculateGcd } from '../../../util/orders'
import TradeOrderPremium from './tradeOrderPremium'

export const orderTypes = [
    {
        value: Broker_OrderType.LIMIT_ORDER,
        label: 'Limit Order'
    },
    {
        value: Broker_OrderType.MARKET_ORDER,
        label: 'Market Order'
    }
]

interface Props {
    hasSlAndTargetAccess: boolean;
    showAllTradeOptions: boolean;
    positions: Position[];
    onSlTargetValidationChange: (isValid: boolean) => void;
}

const TradeOrderAttributes: React.FC<Props> = (props) => {
    const { showAllTradeOptions, hasSlAndTargetAccess, positions, onSlTargetValidationChange } = props;
    const dispatch = useDispatch();
    const user = useSelector(selectUserDetails);
    const product = useSelector(selectTradeProduct)
    const orderType = useSelector(selectOrderType)
    const tradeOrders = useSelector(selectTradeOrders)
    const placedOrders = useSelector(selectOrdersHistory)
    const mode = useSelector(selectStrategyBuilderMode)
    const basketId = useSelector(selectBasketId)
    const { primaryOrUnderlying: selectedSecurity } = useSelectPrimaryOrUnderlyingSecurity()
    const isAdminUser = useMemo(() => isAdmin(user.currentUser), [user])

    const basketPosition = useMemo((): GroupedPosition | null => {
        // For analyze and squareOff modes, use getAnalyzedGroupedPosition
        if ((mode === 'analyze' || mode === 'squareOff') && basketId) {
            return getAnalyzedGroupedPosition(
                positions as unknown as Position[],
                basketId,
                placedOrders,
                tradeOrders
            );
        }
        // For trade mode, create a synthetic position from tradeOrders
        if (mode === 'trade' && tradeOrders?.length && basketId && selectedSecurity) {
            let quantities = []
            let totalPremium = 0
            tradeOrders.forEach(order => {
                const orderQuantity = order.transactionType === TransactionType.BUY ? order.quantity : -order.quantity
                totalPremium += orderQuantity * (order.price ?? 0)
                quantities.push(orderQuantity)
            })
            const netQuantity = calculateGcd(quantities)
            if (netQuantity === 0) return null
            const avgPrice = Math.abs(totalPremium / netQuantity)
            return {
                groupId: basketId,
                groupName: 'Custom',
                quantity: netQuantity,
                avgPrice: avgPrice,
                realizedProfit: 0,
                security: selectedSecurity,
                children: []
            }
        }
        return null
    }, [mode, positions, basketId, placedOrders, tradeOrders, selectedSecurity])

    const orderIdToOrderMap = useMemo(() => {
        if (!placedOrders) return {} as Record<string, Order>
        return placedOrders.reduce(
            (acc, o) => {
                acc[o.orderId] = o
                return acc
            },
            {} as Record<string, Order>
        )
    }, [placedOrders])

    const onProductChange = useCallback((e: any, value: Product) => {
        dispatch(updateTradeProduct(value))
    }, [])

    const onOrderTypeChange = useCallback(
        (event: any) => {
            const orderType = event.target.value as Broker_OrderType
            AnalyticsManager.logEvent('Order Type Changed', { order_type: broker_OrderTypeToJSON(orderType), source: 'Trade' })
            dispatch(updateOrderType(orderType))
        },
        [tradeOrders]
    )

    useEffect(()=>{
        if(!showAllTradeOptions){
            // If not showing all trade options, we can reset the product and order type
            dispatch(updateTradeProduct(Product.INTRADAY))
            dispatch(updateOrderType(Broker_OrderType.LIMIT_ORDER))
        }
    }, [showAllTradeOptions])

    const allExecuted = useMemo(() => tradeOrders.every((o) => orderIdToOrderMap[o.referenceOrderId]?.status === OrderStatus.EXECUTED), [tradeOrders, orderIdToOrderMap])

    // In analyze/squareOff modes, allow SL/Target even when tradeOrders is empty
    const shouldDisableSlTarget = useMemo(() => {
        if (!hasSlAndTargetAccess || !showAllTradeOptions) return true
        if (mode === 'analyze' || mode === 'squareOff') return false
        return false // disable if no changes in fields
    }, [mode, tradeOrders, hasSlAndTargetAccess, showAllTradeOptions])

    return (
        <Grid px={1} container gap={1}>
            { showAllTradeOptions && <> 
            <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                        PRODUCT
                    </Typography>
                    <ToggleButtonGroup
                        id="product-type-group"
                        size="small"
                        value={product}
                        exclusive
                        aria-label="product type"
                        fullWidth
                        disabled={allExecuted}
                        sx={{
                            // mt: 0.5,
                            height: '32px',
                            '.MuiButtonBase-root': {
                                borderColor: (theme) => theme.palette.divider
                            }
                        }}
                        onChange={onProductChange}
                    >
                        <NonInteractiveTooltip title="MIS" arrow placement="top">
                                <ToggleButton
                                    aria-label="mis"
                                    value={Product.INTRADAY}
                                    sx={{
                                        color: (theme) => (product === Product.INTRADAY ? `${theme.palette.text.primary} !important` : `${theme.palette.text.secondary} !important`)
                                    }}
                                >
                                    Intraday
                                </ToggleButton>
                        </NonInteractiveTooltip>
                        <NonInteractiveTooltip title="NRML" arrow placement="top">
                                <ToggleButton
                                    aria-label="overnight"
                                    value={Product.MARGIN}
                                    sx={{
                                        color: (theme) => (product === Product.MARGIN ? `${theme.palette.text.primary} !important` : theme.palette.text.secondary)
                                    }}
                                >
                                    Overnight
                                </ToggleButton>
                        </NonInteractiveTooltip>
                    </ToggleButtonGroup>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                        ORDER TYPE
                    </Typography>
                    <Select
                        value={orderType}
                        size="small"
                        fullWidth
                        // sx={{ fontWeight: 500, ...customStyle.select, height: '32px' }}
                        sx={{ height: '32px' }}
                        disabled={allExecuted}
                        onChange={onOrderTypeChange}
                    >
                        {orderTypes.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            </Grid>
            <TradeOrderPremium disabled={!tradeOrders?.length} />
            {isAdminUser && hasSlAndTargetAccess && <SlAndTargetInputs disabled={shouldDisableSlTarget} disabledCheckbox={false} basketPosition={basketPosition} strategyMode={mode} onValidationChange={onSlTargetValidationChange} />}
            </> }
        </Grid>
    )
}

export default memo(TradeOrderAttributes)